// src/pages/po/OrdemCompraItemsSidePanelContent.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faFileInvoiceDollar } from '@/icons';
import { apiGet, apiPost } from '../../services/api'; // Importado apiPost
import axios from 'axios';
import ScrollableTable from '../../components/common/ScrollableTable';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import Modal from '../../components/common/Modal';
import HistoricoItemModalContent from './HistoricoItemModalContent';
import CompraHistoricoModalContent from './CompraHistoricoModalContent';
import { OrdemCompraItem, OrdemCompraHeader } from '../../types/compras/Compra';
import { HistoricoResponse, CompraHistoricoResponse } from '../../types/compras/Historico';
import './OrdemCompraItemsSidePanelContent.css';
import { formatDateTime, formatDate, getEmptyDetailsMessage } from '../../utils/formatUtils';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import IconExportButton from '../../components/common/IconExportButton';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface OrdemCompraItemsSidePanelContentProps {
  ordemCompra: { id: number; status?: string };
  theme: 'dark' | 'light';
  onClose: () => void;
  onDataChange?: () => void; // Nova prop opcional
}

const OrdemCompraItemsSidePanelContent: React.FC<OrdemCompraItemsSidePanelContentProps> = ({
  ordemCompra,
  theme,
  onClose,
  onDataChange,
}) => {
  const [header, setHeader] = useState<OrdemCompraHeader | null>(null);
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [compraHistoricoData, setCompraHistoricoData] = useState<CompraHistoricoResponse | null>(null);
  const [compraHistoricoModalOpen, setCompraHistoricoModalOpen] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
  const isExportingRef = useRef(false); // guard sincrono contra race do React (setState async)
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const fetchItems = useCallback(
    async (
      pageNumber: number,
      _currentItems: OrdemCompraItem[]
    ): Promise<{ items: OrdemCompraItem[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });
      const endpoint = `/v1/ordens-compra/${ordemCompra.id}/itens?${queryParams.toString()}`;
      const res = await apiGet(endpoint);
            const { dataCriacao, status, usuarioId, nomeUsuario, codFornecedor, nomeFornecedor, nomeLojaEntrega, itens } = res.data.content[0];

      if (pageNumber === 0) {
        setHeader({ dataCriacao, status, usuarioId, nomeUsuario, codFornecedor, nomeFornecedor, nomeLojaEntrega });
      }

      return { items: itens.content, totalItems: itens.page.totalElements };
    },
    [ordemCompra.id]
  );

  const { items, hasMore, isLoading, loadMore, reset } = usePaginatedFetch<OrdemCompraItem>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  useEffect(() => {
    buttonRefs.current = items.map((_, i) => buttonRefs.current[i] || null);
  }, [items]);

  useEffect(() => {
    reset();
  }, [ordemCompra.id, reset]);

const handleHistoricoClick = async (item: OrdemCompraItem, _index: number) => {
  try {
    // Valida codProduto e codCategoriaProduto
    if (!item.codProduto || !item.codCategoriaProduto) {
      await showAlert('Dados do item incompletos. Verifique o produto e a categoria.', { variant: 'warning' });
      return;
    }
    const queryParams = new URLSearchParams({
      codProduto: item.codProduto,
      codCategoriaProduto: item.codCategoriaProduto.toString(),
      itemModuloId: item.requisicaoItemId.toString(),
      page: '0',
      size: '10',
    });
    const res = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
    logger.log('Resposta da API ao carregar histórico:', res.data);
    setHistoricoData(res.data);
    setSelectedItemId(item.requisicaoItemId);
    setHistoricoModalOpen(true);
  } catch (err) {
    logger.error('Erro ao carregar histórico do item:', err);
    // Trata erro 400 com mensagem específica do backend
    if (axios.isAxiosError<{ error?: string }>(err) && err.response?.status === 400 && err.response?.data?.error) {
      await showAlert(err.response.data.error, { variant: 'error' });
    } else {
      await showAlert('Erro ao carregar o histórico. Tente novamente.', { variant: 'error' });
    }
  }
};

  const handleCloseHistoricoModal = () => {
    setHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  };

  const handleCompraHistoricoClick = async (item: OrdemCompraItem) => {
    try {
      if (!item.codProduto || !item.codCategoriaProduto) {
        await showAlert('Dados do item incompletos. Verifique o produto e a categoria.', { variant: 'warning' });
        return;
      }
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        page: '0',
        size: '10',
      });
      const res = await apiGet(`/v1/historico-compras-produto?${queryParams.toString()}`);
      setCompraHistoricoData(res.data);
      setCompraHistoricoModalOpen(true);
    } catch (err) {
      logger.error('Erro ao carregar histórico de compras do produto:', err);
      if (axios.isAxiosError<{ error?: string }>(err) && err.response?.status === 400 && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar o histórico de compras. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleCloseCompraHistoricoModal = () => {
    setCompraHistoricoModalOpen(false);
    setCompraHistoricoData(null);
  };

  const handleExport = async () => {
    if (isExportingRef.current) return; // bloqueia reentrada se cliques chegam antes do React renderizar disabled
    isExportingRef.current = true;
    setIsLoadingExport(true);
    try {
      const status = ordemCompra.status || header?.status || 'ABERTA';
      const response = await apiPost('/v1/ordens-compra/export-excel', {
        ordemCompraIds: [ordemCompra.id],
        status,
      }, {
        responseType: 'blob',
        blocking: true,
      });

      logger.log('Resposta da API:', response.data);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordens-compra-${status.toLowerCase()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Toast.success('Arquivo Excel gerado com sucesso!');
      onDataChange?.(); // Sinaliza alteração de dados
    } catch (err) {
      logger.error('Erro ao exportar para Excel:', err);
      let errorMessage = 'Erro ao gerar o arquivo Excel. Tente novamente.';
      if (axios.isAxiosError<{ error?: string }>(err)) {
        errorMessage = err.response?.data?.error || errorMessage;
        if (err.response?.status === 400) {
          errorMessage = 'O servidor retornou um erro: formato ou dados inválidos.';
        }
      }
      await showAlert(errorMessage, { variant: 'error' });
    } finally {
      setIsLoadingExport(false);
      isExportingRef.current = false;
    }
  };

  const columns = [
    {
      header: 'Código',
      render: (item: OrdemCompraItem) => item.codProduto,
    },
    {
      header: 'Cód. Categoria',
      render: (item: OrdemCompraItem) => item.codCategoriaProduto,
    },
    {
      header: 'Nome do Produto',
      render: (item: OrdemCompraItem) => item.nomeProduto,
    },
    {
      header: 'Categoria',
      render: (item: OrdemCompraItem) => item.nomeCategoria,
    },
    {
      header: 'Quantidade Compra',
      render: (item: OrdemCompraItem) => item.qtdCompra,
    },
    {
      header: 'Quantidade Extra',
      render: (item: OrdemCompraItem) => item.qtdCompraExtra,
    },
    {
      header: 'Quantidade Total',
      render: (item: OrdemCompraItem) => item.qtdCompraOriginal,
    },
    {
      header: 'Requisição',
      render: (item: OrdemCompraItem) => item.requisicaoId,
    },
    {
      header: 'Prioridade',
      render: (item: OrdemCompraItem) => {
        const prioridade = item.prioridade || 'Não definida';
        if (prioridade === 'URGENTE') {
          return <span className="urgent-priority">{prioridade}</span>;
        }
        return prioridade;
      },
    },
    {
      header: 'Loja',
      render: (item: OrdemCompraItem) => item.nomeFantasiaLoja,
    },
    {
      header: 'Solicitante',
      render: (item: OrdemCompraItem) => item.nomePessoaUsuario,
    },
  ];

  return (
    <div className={`ordem-compra-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isLoading} />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>
              Ordem de Compra Nº {ordemCompra.id} | Criada em{' '}
              {header ? formatDateTime(header.dataCriacao) : 'Carregando...'}
            </h3>
          </div>
          <div className="facelet-p">
            <p>Comprador(a): {header?.nomeUsuario || 'Erro de Integração'} | Status: {header?.status || 'Carregando...'}</p>
            <p>
              {header?.codFornecedor ? `Código Fornecedor: ${header.codFornecedor} | ` : ''} Fornecedor: {header?.nomeFornecedor || 'Não informado'} | Loja de Entrega: {header?.nomeLojaEntrega || 'Não informada'}
            </p>
          </div>
        </div>
        <div>
          <div className="header-actions">
            <IconExportButton
              onClick={handleExport}
              title="Exportar para Excel"
              disabled={isLoadingExport}
            />
          </div>
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <>
          <ScrollableTable<OrdemCompraItem>
            data={items}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={isLoading}
            columns={columns}
            onSelectChange={() => {}}
            renderHistoryAction={(item: OrdemCompraItem, index: number) => (
              <button
                className="action-button"
                ref={(el) => {
                  buttonRefs.current[index] = el;
                }}
                onClick={() => handleHistoricoClick(item, index)}
                title="Ver Histórico do Item"
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </button>
            )}
            renderPurchaseHistoryAction={(item: OrdemCompraItem) => (
              <button
                className="action-button"
                onClick={() => handleCompraHistoricoClick(item)}
                title="Ver Histórico de Compras"
              >
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
              </button>
            )}
            fetchDetails={async (item: OrdemCompraItem) => { // Modificado: mantém ?tipoRequisicao=${item.tipoRequisicao} na rota, usando exatamente o valor do item da linha
              const res = await apiGet(`/v1/historico-itens/${item.requisicaoItemId}/po/requisicao-item/detalhes?tipoRequisicao=${item.tipoRequisicao}`);
              return res.data.content;
            }}
            detailsColumns={[
              { header: 'Requisição', accessor: 'requisicao' },
              { header: 'Cotação', accessor: 'preOrdem' },
              { header: 'Ordem de Compra', accessor: 'ordemCompra' },
              { header: 'Qtd. Compra', accessor: 'qtdCompraOriginal' },
              { header: 'Qtd. Recebida', accessor: 'qtdRecebida' },
              { header: 'Status Recebimento', accessor: 'statusRecebimento' },
              { header: 'Comprador', accessor: 'comprador' },
              { header: 'Fornecedor', accessor: 'nomeFornecedor' },
              { header: 'Previsão Chegada', accessor: 'previsaoChegada', formatter: formatDate },
              { header: 'Conferência', accessor: 'recebimentoConferencia' },
              { header: 'Recebimento', accessor: 'dataConferencia', formatter: formatDateTime },
              { header: 'Conferente', accessor: 'conferente' },
              { header: 'Nº Nota Fiscal', accessor: 'nroNotaFiscal' },
            ]}
            emptyDetailsMessage={(item: OrdemCompraItem) => getEmptyDetailsMessage(item.tipoRequisicao)} // Modificado: usa exatamente item.tipoRequisicao (o mesmo da rota) para a mensagem vazia
            emptyMessage="Nenhum item encontrado para esta ordem de compra." // Corrigido: ajusta mensagem para contexto de ordem de compra
            showCheckboxes={false} // Corrigido: remove showCheckboxes dinâmico (não aplica aqui) e mantém false
            showTotalItems={true} // Corrigido: remove renderCheckbox (não existe nesse componente)
          />
          <div className="form-actions">
          </div>
          <Modal
            isOpen={historicoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            zIndex={2000}
          >
            {historicoData && selectedItemId && (
              <HistoricoItemModalContent
                historico={historicoData}
                requisicaoItemId={selectedItemId}
                isOpen={historicoModalOpen}
                onClose={handleCloseHistoricoModal}
                theme={theme}
                showCancelButton={false}
              />
            )}
          </Modal>
          <Modal
            isOpen={compraHistoricoModalOpen}
            onClose={handleCloseCompraHistoricoModal}
            theme={theme}
            zIndex={2000}
          >
            {compraHistoricoData && (
              <CompraHistoricoModalContent
                compraHistorico={compraHistoricoData}
                isOpen={compraHistoricoModalOpen}
                onClose={handleCloseCompraHistoricoModal}
                theme={theme}
                showCancelButton={false}
              />
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default OrdemCompraItemsSidePanelContent;